import { GetServerSidePropsContext } from "next";
import Image from "next/image";
import dayjs from "dayjs";
import advancedFormat from "dayjs/plugin/advancedFormat";
import axios from "axios";
import { useEffect, useState } from "react";
import { CalendarIcon, ClockIcon } from "@heroicons/react/24/outline";
import { Button } from "@/components/ui/button";
import BootcampTabs, { formatNumber } from "@/components/bootcamp/BootcampTabs";
import BootcampForm from "@/components/bootcamp/Bootcampform";
// import Footer from "@/components/Footer";
import { useRouter } from "next/router";
import { BsStarFill, BsStarHalf } from "react-icons/bs";
import useGetAmount from "@/hooks/getAmount";
import mixpanel from "@/utils/mixpanel";
import { ArrowLeftIcon } from "lucide-react";
import webengage from "@/webengage";
import Breadcrumb from "@/components/BreadCrumb";
import Head from "next/head";
import dynamic from "next/dynamic";

const Footer = dynamic(() => import("@/components/Footer"), { ssr: false });

dayjs.extend(advancedFormat);

export async function getServerSideProps(
  context: GetServerSidePropsContext<{ id: string }>
) {
  const slug = context.params?.id;
  if (!slug)
    return {
      notFound: true,
    };

  let bootcampCoupons;
  try {
    const { data } = await axios.get(
      `${process.env.NEXT_PUBLIC_BASE_API}coupons/bootcamp/${slug}`,
      {
        headers: {
          "Accept-Encoding": "gzip",
        },
      }
    );

    bootcampCoupons = data.data;
  } catch (error) {
    console.error("ERROR while fetching bootcamp coupons", error);
    bootcampCoupons = [];
  }

  let bootcamp;
  try {
    const { data } = await axios.get(
      `${process.env.NEXT_PUBLIC_BASE_API}bootcamps/bootcamp-details/${slug}`,
      {
        headers: {
          "Accept-Encoding": "gzip",
        },
      }
    );
    bootcamp = data.data;
  } catch (error) {
    console.error("Error fetching bootcamp by slug", error);
  }
  if (!bootcamp)
    return {
      notFound: true,
    };
  return {
    props: {
      bootcamp: bootcamp,
      coupons: bootcampCoupons,
    },
  };
}

export default function SingleBootcampPage({
  bootcamp,
  coupons,
}: {
  bootcamp: any;
  coupons: any;
}) {
  const router = useRouter();
  const [starRating, setStarRating] = useState<number>(0);
  const [reviews, setReviews] = useState<number>(0);
  const [years, setYears] = useState<number>(0);
  const [students, setStudents] = useState<number>(0);

  const amount = useGetAmount({
    baseAmount: bootcamp?.discountedPrice.toString(),
    baseBundleAmount:
      bootcamp?.bundleBootcamp?.discountedPrice?.toString() ?? "",
    token: router.query?.token,
    originalAmount: bootcamp?.price.toString(),
  });

  useEffect(() => {
    if (coupons) {
      localStorage.setItem("coupons", JSON.stringify(coupons));
    }
  }, [coupons]);

  useEffect(() => {
    if (bootcamp.teacher?.achievements) {
      const achievements = bootcamp.teacher.achievements.split(";");
      setStarRating(achievements[0] ?? 0);
      setReviews(achievements[1] ?? 0);
      setYears(achievements[4] ?? 0);
      setStudents(achievements[2] ?? 0);
    }
  }, [bootcamp?.teacher?.achievements]);

  let activeBatch: any;
  function getStartDate(batch: any) {
    for (let i = 0; i < batch?.length; i++) {
      if (batch[i]?.active) {
        activeBatch = batch[i];
        const endDateTime: any = new Date(batch[i]?.endDateTime);
        const startDateTime: any = new Date(batch[i]?.startDateTime);
        const duration = Math.floor(
          Math.abs(endDateTime - startDateTime) / (1000 * 60 * 60 * 24)
        );
        return {
          startDateTime: dayjs(batch[i]?.startDateTime),
          duration: duration,
        };
      }
    }
    return { startDateTime: dayjs(), duration: 1 };
  }

  const startDateInfo = getStartDate(bootcamp?.Batches);

  return (
    <>
      <Head>
        <title>Paid BootCamp | {bootcamp?.teacher?.name} | Astrolearn</title>
        <meta
          name="description"
          content={bootcamp?.subHeading || bootcamp?.description}
        />
      </Head>
      <main className="min-h-screen h-full space-y-4 bg-[#EEEBE6] font-lato grid grid-cols-1">
        <div className="p-4 md:p-8 lg:p-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 pb-10 bg-[#EEEBE6] -mb-4">
          <div className="lg:col-span-2 space-y-4">
            <Breadcrumb />
            <div className="flex gap-4">
              <ArrowLeftIcon
                className="w-6 h-6 mt-0.5 hover:cursor-pointer md:hidden"
                onClick={() => router.back()}
              />
              <h1 className="text-xl md:text-2xl lg:text-3xl font-semibold">
                {bootcamp?.title}
              </h1>
            </div>
            <section className="grid lg:grid-cols-2 gap-4">
              <Image
                className="rounded-lg shadow-lg"
                src={bootcamp?.coverImage || "/common/fallback-cover.webp"}
                alt="thumbnail"
                width={1000}
                height={500}
              />

              <div className="p-4 pb-0 flex flex-col gap-4 justify-between">
                <div className="flex md:flex-col gap-4 justify-between">
                  <span className="flex items-center gap-1">
                    <CalendarIcon width={16} height={16} className="mr-1" />
                    <span className="max-md:hidden">Starts on</span>
                    <span className="font-semibold md:text-[#9A7026] max-md:hidden">
                      {startDateInfo?.startDateTime?.format("DD MMMM")} (
                      {startDateInfo?.startDateTime?.format("dddd")})
                    </span>
                    <span className="md:hidden text-sm sm:text-base">
                      {startDateInfo?.startDateTime?.format("Do MMMM (ddd)")}
                    </span>
                  </span>
                  <span className="flex items-center gap-1 text-sm sm:text-base">
                    <ClockIcon width={16} height={16} className="mr-1" />
                    Duration:
                    <span className="md:font-semibold md:text-[#9A7026]">
                      {startDateInfo?.duration}{" "}
                      {startDateInfo?.duration > 1 ? "days" : "day"}
                    </span>
                  </span>
                </div>
                <div className="bg-black h-[1px] w-full" />
                <div className="flex flex-row-reverse max-md:justify-between md:flex-col gap-4">
                  <div className="flex items-center gap-1 max-md:flex-col flex-row">
                    <span className="max-md:hidden">Rating:</span>
                    <span className="flex items-center">
                      <BsStarFill width={14} height={14} fill="#9A7026" />
                      <BsStarFill width={14} height={14} fill="#9A7026" />
                      <BsStarFill width={14} height={14} fill="#9A7026" />
                      <BsStarFill width={14} height={14} fill="#9A7026" />
                      <BsStarHalf width={14} height={14} fill="#9A7026" />
                    </span>
                    <span className="text-sm">
                      ({formatNumber(students === 0 ? 10000 : students)}+
                      students)
                    </span>
                  </div>
                  <div className="flex flex-col gap-2">
                    <span className="max-md:hidden text-xl">
                      {amount.isToken
                        ? "Token"
                        : amount.isDiscounted
                        ? "Discounted"
                        : "Exclusive"}{" "}
                      Price
                    </span>
                    <div className="flex max-md:flex-col flex-row items-center md:gap-4">
                      <strong className="text-xl max-md:font-semibold md:text-3xl lg:text-4xl text-[#9A7026] font-extrabold">
                        {Number(
                          amount.isToken || amount?.isDiscounted
                            ? amount.mainButtonAmount
                            : bootcamp?.discountedPrice
                        ).toLocaleString("en-US", {
                          style: "currency",
                          currency: "INR",
                          minimumFractionDigits: 0,
                        })}
                      </strong>
                      <span className="text-sm md:text-xl lg:text-2xl text-zinc-500 line-through">
                        {Number(bootcamp?.price).toLocaleString("en-US", {
                          style: "currency",
                          currency: "INR",
                          minimumFractionDigits: 0,
                        })}
                      </span>
                    </div>
                    {amount.isToken && (
                      <div className="text-foreground text-sm font-medium">
                        This is a token payment. Please pay{" "}
                        <span className="font-bold">
                          ₹{amount.mainButtonAmount}
                        </span>{" "}
                        now and the remaining amount of{" "}
                        <span className="font-bold">
                          ₹
                          {parseInt(amount.BaseAmount, 10) -
                            parseInt(amount.mainButtonAmount, 10)}
                        </span>{" "}
                        will be due later.
                      </div>
                    )}
                  </div>
                </div>
                <a href="#form" className="md:hidden w-full">
                  <Button
                    className="rounded-lg font-semibold text-xl py-6 uppercase w-full"
                    onClick={() => {
                      const _webengage = webengage();
                      if (_webengage && bootcamp) {
                        _webengage.track("Payment initiated", {
                          title: bootcamp?.title,
                          industry: "finance",
                          fees: Number(bootcamp?.discountedPrice),
                          instructor: bootcamp?.teacher.name,
                          duration: bootcamp?.duration,
                          source: "Astrolearn",
                          cohort: bootcamp?.startDateTime,
                          paymentType: amount.isToken
                            ? "token"
                            : "complete payment",
                        });
                      }
                      if (_webengage && bootcamp) {
                        _webengage.track("Bootcamps registration initiated", {
                          title: bootcamp?.title,
                          instructor: bootcamp?.teacher?.name,
                          fees: Number(bootcamp?.discountedPrice),
                          source: "Astrolearn",
                          industry: bootcamp?.category?.name,
                        });
                      }
                      mixpanel.track("enroll_now", {
                        bootcamp: bootcamp?.title,
                        button_location: "top_cta",
                        source: "Astrolearn",
                      });
                    }}
                  >
                    Enroll Now
                  </Button>
                </a>
              </div>
            </section>
            <BootcampTabs
              years={years}
              starRating={starRating}
              reviews={reviews}
              students={students}
              bootcamp={bootcamp}
            />
            <div className="md:hidden">
              <BootcampForm
                amount={amount}
                bootcamp={bootcamp}
                coupons={coupons}
              />
            </div>
          </div>
          <div className="max-md:hidden">
            <BootcampForm
              amount={amount}
              bootcamp={bootcamp}
              coupons={coupons}
            />
          </div>
        </div>
        <Footer phone="+918048822399" />
      </main>
    </>
  );
}
